import {
  richTextPlainText,
  toSectionBlockInput,
  type ContentBlock,
  type ImageWidth,
  type RichTextDocument,
  type SectionBlockInput,
  type SectionBlockType,
} from '@oscom/shared';

export type TextBlockType = 'richText' | 'note' | 'warning';

/**
 * A block while it is edited. An image block exists before its upload went
 * through; until it has an `assetId` it stays local and is not saved.
 */
export type EditorBlock =
  | { id: string; type: TextBlockType; content: RichTextDocument }
  | {
      id: string;
      type: 'image';
      assetId?: string;
      alternativeText: string;
      caption: string;
      widthPercent: ImageWidth;
      uploading?: boolean;
      uploadError?: string;
    };

export function newBlockId(): string {
  return `blk_${crypto.randomUUID()}`;
}

export function emptyRichText(): RichTextDocument {
  return { type: 'doc', content: [{ type: 'paragraph' }] };
}

export function newBlock(type: SectionBlockType, id: string = newBlockId()): EditorBlock {
  return type === 'image'
    ? { id, type: 'image', alternativeText: '', caption: '', widthPercent: 100 }
    : { id, type, content: emptyRichText() };
}

export function fromStoredBlocks(blocks: readonly ContentBlock[]): EditorBlock[] {
  return [...blocks]
    .sort((a, b) => a.order - b.order)
    .flatMap((block): EditorBlock[] => {
      const input = toSectionBlockInput(block);
      if (!input) return [];
      if (input.type !== 'image') return [input];
      return [
        {
          id: input.id,
          type: 'image',
          assetId: input.assetId,
          alternativeText: input.alternativeText,
          caption: input.caption ?? '',
          widthPercent: input.widthPercent,
        },
      ];
    });
}

/** What the API stores; image blocks without an uploaded asset stay local. */
export function toBlockInputs(blocks: readonly EditorBlock[]): SectionBlockInput[] {
  return blocks.flatMap((block): SectionBlockInput[] => {
    if (block.type !== 'image') return [{ id: block.id, type: block.type, content: block.content }];
    if (!block.assetId) return [];
    const caption = block.caption.trim();
    return [
      {
        id: block.id,
        type: 'image',
        assetId: block.assetId,
        alternativeText: block.alternativeText.trim(),
        ...(caption ? { caption } : {}),
        widthPercent: block.widthPercent,
      },
    ];
  });
}

export function moveBlock(
  blocks: readonly EditorBlock[],
  index: number,
  delta: -1 | 1,
): EditorBlock[] {
  const target = index + delta;
  if (index < 0 || index >= blocks.length || target < 0 || target >= blocks.length) {
    return [...blocks];
  }
  const next = [...blocks];
  const [moved] = next.splice(index, 1);
  if (moved) next.splice(target, 0, moved);
  return next;
}

const PREFIX: Record<TextBlockType, string> = {
  richText: '',
  note: 'Hinweis: ',
  warning: 'Warnung: ',
};

/** The user's own version as plain text - shown in "Ihre Fassung" after a conflict. */
export function editorDraftText(title: string, blocks: readonly EditorBlock[]): string {
  const parts = [title];
  for (const block of blocks) {
    if (block.type === 'image') {
      const alt = block.alternativeText ? `: ${block.alternativeText}` : '';
      const caption = block.caption ? ` – ${block.caption}` : '';
      parts.push(`[Bild${alt}${caption}]`);
    } else {
      parts.push(PREFIX[block.type] + richTextPlainText(block.content));
    }
  }
  return parts.join('\n\n');
}
